import { useEffect, useRef, useCallback, useState } from 'react';
import { flushSync } from 'react-dom';

const useInactivityTimer = ({
  timeout = 30 * 60 * 1000,
  warningTime = 60 * 1000,
  onTimeout,
  onContinue,
  enabled = true,
}) => {
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState(Math.ceil(warningTime / 1000));
  
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  // Use ref to track warning state synchronously (avoid race condition)
  const isWarningActiveRef = useRef(false);

  const activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'wheel',
  ];

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(() => {
    clearAllTimers();
    isWarningActiveRef.current = false;
    setIsWarningVisible(false);
    if (onTimeout) {
      onTimeout();
    }
  }, [clearAllTimers, onTimeout]);

  const showWarning = useCallback(() => {
    // Set ref first to prevent race condition with handleActivity
    isWarningActiveRef.current = true;
    setIsWarningVisible(true);
    setRemainingTime(Math.ceil(warningTime / 1000));

    countdownIntervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, warningTime);
  }, [warningTime, handleTimeout]);

  const startTimer = useCallback(() => {
    clearAllTimers();
    lastActivityRef.current = Date.now();

    // Show warning after full timeout period, then countdown starts
    warningTimeoutRef.current = setTimeout(() => {
      showWarning();
    }, timeout);
  }, [clearAllTimers, timeout, showWarning]);

  const handleActivity = useCallback(() => {
    // Use ref to check warning state synchronously (avoid race condition)
    if (!isWarningActiveRef.current && enabled) {
      lastActivityRef.current = Date.now();
      startTimer();
    }
  }, [enabled, startTimer]);

  const handleContinue = useCallback(() => {
    clearAllTimers();
    isWarningActiveRef.current = false;
    setIsWarningVisible(false);
    setRemainingTime(Math.ceil(warningTime / 1000));
    lastActivityRef.current = Date.now();
    
    if (onContinue) {
      onContinue();
    }
    
    startTimer();
  }, [clearAllTimers, onContinue, startTimer, warningTime]);

  const handleLogout = useCallback(() => {
    handleTimeout();
  }, [handleTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    startTimer();

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      clearAllTimers();
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, startTimer, handleActivity, clearAllTimers]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && enabled && !isWarningActiveRef.current) {
        const elapsed = Date.now() - lastActivityRef.current;
        
        // If elapsed time exceeds timeout + warningTime, logout immediately
        if (elapsed >= timeout + warningTime) {
          handleTimeout();
        } else if (elapsed >= timeout) {
          // If past timeout but within warning period, show warning
          showWarning();
        } else {
          startTimer();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, timeout, warningTime, handleTimeout, showWarning, startTimer]);

  return {
    isWarningVisible,
    remainingTime,
    handleContinue,
    handleLogout,
    resetTimer: handleActivity,
  };
};

export default useInactivityTimer;
